/* eslint-disable react/display-name */
import React, { useState, useEffect, forwardRef, useRef } from 'react';
import { createPortal } from 'react-dom';
import classnames from 'classnames';
import { IconChevronDown } from '@pentabd/icons';

import useOnClickOutside from '../../hooks/useOnClickOutside';
import useEventListener from '../../hooks/useEventListener';

import { hasAlready } from '../../utils/arrayOperation';
import { HEIGHT } from './helpers';
import { OptionType, PositionType, SelectProps, SelectionType } from './types';
import SelectedPanel from './SelectedPanel';
import ControlPanel from './ControlPanel';
import SelectMenu from './SelectMenu';

export const Select = forwardRef<HTMLInputElement, SelectProps>(
  (
    {
      name,
      status,
      size = 'md',
      placeholder = 'Select ...',
      options = [],
      isMulti = false,
      isSearchable = false,
      onChange,
      disabled,
      value,
      defaultValue,
      defaultValues,
      prefix,
      suffix = <IconChevronDown size="20" fill="subtitle2" />,
      stablePrefix = true,
      stableSuffix = true,
      renderSelected,
      onSelectItem,
      numberOfSelection,
      portal,
      minWidth,
      emptyMessage,
      zIndexValue,
      isCheckable,
      isRequiredSelection,
      dropdownHeightType,
      testId,
      isTextEllipsis,
      isAutoFocus = true,
      isFixedWidthSelectedView,
      ...props
    }: SelectProps,
    ref,
  ) => {
    //States
    const [effectiveOptions, setEffectiveOptions] =
      useState<OptionType[]>(options);
    const [showMenu, setShowMenu] = useState<boolean>(false);
    const [selectedValues, setSelectedValues] = useState<SelectionType>(
      isMulti
        ? options?.filter((item) =>
            defaultValues?.includes(item.value as never),
          )
        : defaultValue
        ? options?.find((item) => item.value === defaultValue)
        : null,
    );
    const [searchText, setSearchText] = useState<string>('');
    // const [scroll, setScroll] = useState(0);
    const [dropUp, setDropUp] = useState(false);
    const [position, setPosition] = useState<PositionType>({});

    const height = // TODO:: need refactor
      dropdownHeightType === 'sm'
        ? HEIGHT
        : dropdownHeightType === 'md'
        ? 256
        : dropdownHeightType === 'lg'
        ? 320
        : HEIGHT;

    useEffect(() => {
      setEffectiveOptions(options);
    }, [options]);

    useEffect(() => {
      setSelectedValues(
        isMulti
          ? options?.filter((item) =>
              defaultValues?.includes(item.value as never),
            )
          : defaultValue
          ? options?.find((item) => item.value === defaultValue)
          : null,
      );
    }, [options, defaultValue, defaultValues]);

    // reference
    const wrapperRef = useRef<HTMLDivElement>(null);
    const inputWrapperRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // click outside component
    const handleClickOutside = () => {
      if (!portal) {
        setShowMenu(false);
      }
    };

    useOnClickOutside(wrapperRef, handleClickOutside);

    //click outside dropdown
    const handleClickOutsideDropdown = () => {
      if (portal && showMenu) {
        setShowMenu(false);
      }
    };

    useOnClickOutside(dropdownRef, handleClickOutsideDropdown);

    // scroll event
    const onScroll = () => {
      if (portal) {
        // setScroll(window.scrollY);
        showMenu && setShowMenu(false);
      }
    };

    useEventListener('scroll', onScroll);

    //Actions handler
    const updateEffectOptions = (): void => {
      let updatedOptions: OptionType[] = [];
      if (searchText)
        updatedOptions = options.filter((option) => {
          return option.label.toUpperCase().includes(searchText.toUpperCase());
        });
      else updatedOptions = options;

      setEffectiveOptions(updatedOptions);
    };

    // const resetSelectedValues = (): void => {
    //   if (Array.isArray(selectedValues)) setSelectedValues([]);
    //   setSelectedValues(null);
    // };

    // const resetSearchText = (): void => setSearchText('');

    const removeSelectedValues = (removeOption: OptionType): void => {
      const updatedSelectedValues = (selectedValues as OptionType[]).filter(
        (selectedValue) => selectedValue.value !== removeOption.value,
      );

      setSelectedValues(updatedSelectedValues);
      onSelectItem &&
        onSelectItem(updatedSelectedValues.map((item) => item.value) as []);
    };

    const addSelectedValues = (addOption: OptionType): void => {
      const updatedSelectedValues = [
        ...(selectedValues as OptionType[]),
        addOption,
      ];

      setSelectedValues(updatedSelectedValues);
      onSelectItem &&
        onSelectItem(updatedSelectedValues.map((item) => item.value) as []);
    };

    const handleChangeOnSearchText = (text: string): void =>
      setSearchText(text);

    const handleClickOnControlPanel = () => {
      if (!disabled) {
        if (!isMulti && !isSearchable) {
          setShowMenu((prev: boolean): boolean => !prev);
        } else {
          setShowMenu(true);
        }
      }
      if (portal) {
        if (wrapperRef.current) {
          setPosition(wrapperRef.current?.getBoundingClientRect());
        }

        const windowHeight = window.innerHeight;
        const instOffsetWithMenu =
          wrapperRef.current.getBoundingClientRect().bottom + height;

        setDropUp(instOffsetWithMenu >= windowHeight);
      }
    };

    const handleClickOnOption = (currentOption: OptionType): void => {
      if (isMulti) {
        const multiSelectedValues = selectedValues as [];
        if (
          numberOfSelection &&
          multiSelectedValues?.length >= numberOfSelection
        ) {
          if (Array.isArray(selectedValues)) {
            if (hasAlready(selectedValues, 'value', currentOption.value)) {
              removeSelectedValues(currentOption);
            }
          }
          return;
        }
        if (Array.isArray(selectedValues)) {
          if (hasAlready(selectedValues, 'value', currentOption.value)) {
            removeSelectedValues(currentOption);
          } else {
            addSelectedValues(currentOption);
          }
        }
      } else {
        setSelectedValues(currentOption);
        onSelectItem && onSelectItem(currentOption.value);
        setShowMenu(false);
      }
      if (searchText) {
        setSearchText('');
      }
    };

    const handleSelectAll = (): void => {
      if (isMulti && isCheckable) {
        if (effectiveOptions?.length !== (selectedValues as [])?.length) {
          setSelectedValues([...effectiveOptions]);
          onSelectItem &&
            onSelectItem(effectiveOptions.map((item) => item.value) as []);
        } else {
          setSelectedValues([]);
          onSelectItem && onSelectItem([]);
        }
      }
      if (searchText) {
        setSearchText('');
      }
    };

    //Child Components
    const getSearchField = (
      hasPlaceholder: boolean,
    ): string | JSX.Element | JSX.Element[] => {
      return (
        <input
          {...props}
          name={name}
          value={searchText}
          placeholder={placeholder && hasPlaceholder ? placeholder : ''}
          type="text"
          onChange={(e) => handleChangeOnSearchText(e.target.value)}
          disabled={disabled}
          className="w-100"
          autoFocus={isAutoFocus}
        />
      );
    };

    const getMenu = () => (
      <SelectMenu
        portal={portal}
        dropUp={dropUp}
        effectiveOptions={effectiveOptions}
        inputWrapperRef={inputWrapperRef}
        position={position}
        size={dropdownHeightType}
        zIndexValue={zIndexValue}
        dropdownRef={dropdownRef}
        isMulti={isMulti}
        placeholder={placeholder}
        emptyMessage={emptyMessage}
        handleClickOnOption={handleClickOnOption}
        // scroll={scroll}
        isCheckable={isCheckable}
        handleSelectAll={handleSelectAll}
        selectedValues={selectedValues}
        isRequiredSelection={isRequiredSelection}
        numberOfSelection={numberOfSelection}
        testId={testId}
      />
    );

    useEffect(() => {
      updateEffectOptions();
    }, [searchText]);

    return (
      <div
        className={classnames('position-relative', {
          'select-min-width': minWidth,
        })}
        ref={wrapperRef}
      >
        <div
          ref={inputWrapperRef}
          className={classnames(
            'select-wrap d-flex align-items-center flex-wrap pointer',
            {
              'select-wrap-disabled': disabled,
              [`select-wrap-${size}`]: size,
              [`select-wrap-${status}`]: status && showMenu,
            },
          )}
          data-testid={testId}
          onClick={handleClickOnControlPanel}
        >
          {!selectedValues ||
          (Array.isArray(selectedValues) && selectedValues.length === 0) ? (
            <ControlPanel
              prefix={prefix}
              isSearchable={isSearchable}
              showMenu={showMenu}
              getSearchField={getSearchField}
              placeholder={placeholder}
              suffix={suffix}
            />
          ) : (
            <SelectedPanel
              prefix={prefix}
              stablePrefix={stablePrefix}
              selectedValues={selectedValues}
              renderSelected={renderSelected}
              removeSelectedValues={removeSelectedValues}
              isSearchable={isSearchable}
              isTextEllipsis={isTextEllipsis}
              showMenu={showMenu}
              getSearchField={getSearchField}
              suffix={suffix}
              stableSuffix={stableSuffix}
              disabled={disabled}
              isFixedWidthSelectedView={isFixedWidthSelectedView}
            />
          )}
          <input
            ref={ref}
            name={name}
            value={value}
            defaultValue={isMulti ? defaultValues : (defaultValue as any)}
            hidden
            onChange={onChange}
            readOnly
          />
        </div>
        {!portal && showMenu ? getMenu() : null}
        {portal && createPortal(showMenu ? getMenu() : null, document.body)}
      </div>
    );
  },
);
